import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { format, parseISO, subDays, startOfDay, addDays } from "date-fns";
import api from "../api/client";
import type { Stats } from "../types";

const COLORS = [
  "#8b5cf6",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#ec4899",
  "#84cc16",
  "#f97316",
];

// Helper to extract short repo name from git URL
const extractRepoName = (url: string | null) => {
  if (!url || url === "local") return "local";
  const match = url.match(/\/([^/]+)\.git$/);
  return match ? match[1] : url;
};

export function Dashboard() {
  useEffect(() => {
    document.title = "vibe-check ⏵Dashboard";
  }, []);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadStats();
  }, [days]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await api.getStats(days);
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  // Get unique repos from daily activity data (must be before early returns)
  const repos = useMemo(() => {
    if (!stats?.daily_activity_by_repo) return [];
    return [
      ...new Set(
        stats.daily_activity_by_repo.map((d) => extractRepoName(d.repository)),
      ),
    ];
  }, [stats?.daily_activity_by_repo]);

  // Transform daily activity by repo into chart format
  // Each date becomes a row with a column for each repo
  // Fills in all dates in the range including today
  const chartDataByRepo = useMemo(() => {
    if (!stats?.daily_activity_by_repo) return [];

    const dateMap = new Map<string, Record<string, string | number>>();

    // Generate all dates in the range (from N days ago to today)
    const today = startOfDay(new Date());
    const startDate = subDays(today, days - 1);
    let currentDate = startDate;
    while (currentDate <= today) {
      const dateStr = format(currentDate, "yyyy-MM-dd");
      dateMap.set(dateStr, { rawDate: dateStr });
      currentDate = addDays(currentDate, 1);
    }

    // Collect all repos
    const allRepos = new Set(
      stats.daily_activity_by_repo.map((d) => extractRepoName(d.repository)),
    );

    // Fill in repo counts from the data
    stats.daily_activity_by_repo.forEach((d) => {
      const existing = dateMap.get(d.date) || { rawDate: d.date };
      existing[extractRepoName(d.repository)] = d.count;
      dateMap.set(d.date, existing);
    });

    // Fill in 0 for any repos missing on a given day
    dateMap.forEach((entry) => {
      allRepos.forEach((repo) => {
        if (entry[repo] === undefined) entry[repo] = 0;
      });
    });

    // Convert to array and format dates
    return Array.from(dateMap.values())
      .sort((a, b) => String(a.rawDate).localeCompare(String(b.rawDate)))
      .map((d) => ({
        ...d,
        date: format(parseISO(String(d.rawDate)), "MMM d"),
      }));
  }, [stats?.daily_activity_by_repo, days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
        <p className="text-red-400">{error}</p>
        <button
          onClick={loadStats}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return <p className="text-gray-400">No data available</p>;
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-gray-700 text-white rounded px-3 py-2 border border-gray-600"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Total Events</p>
          <p className="text-3xl font-bold text-white">
            {stats.totals.total_events.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Total Sessions</p>
          <p className="text-3xl font-bold text-white">
            {stats.totals.total_sessions.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Active Days</p>
          <p className="text-3xl font-bold text-white">
            {stats.totals.active_days.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Activity Chart */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">
          Daily Activity by Repository
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartDataByRepo}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1f2937",
                  border: "1px solid #374151",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#fff" }}
              />
              <Legend />
              {repos.map((repo, index) => (
                <Line
                  key={repo}
                  type="linear"
                  dataKey={repo}
                  name={repo}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Events by Type */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            Events by Type
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.by_type}
                  dataKey="count"
                  nameKey="event_type"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(props) => {
                    const { name, percent } = props as {
                      name: string;
                      percent: number;
                    };
                    return `${name || "unknown"} (${((percent ?? 0) * 100).toFixed(0)}%)`;
                  }}
                  labelLine={false}
                >
                  {stats.by_type.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "1px solid #374151",
                    borderRadius: "8px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Repositories */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            Top Repositories
          </h2>
          <div className="space-y-3">
            {stats.by_repository.map((repo, index) => (
              <div key={index} className="flex justify-between items-center">
                <Link
                  to={`/messages?git_remote=${encodeURIComponent(repo.repository)}`}
                  className="text-gray-300 truncate max-w-[200px] hover:text-purple-400 transition-colors underline decoration-gray-600 hover:decoration-purple-400"
                >
                  {extractRepoName(repo.repository)}
                </Link>
                <span className="text-purple-400 font-medium">
                  {repo.count.toLocaleString()}
                </span>
              </div>
            ))}
            {stats.by_repository.length === 0 && (
              <p className="text-gray-500">No repository data</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">
          Recent Sessions
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700">
                <th className="pb-2">Session ID</th>
                <th className="pb-2">Repository</th>
                <th className="pb-2">Started</th>
                <th className="pb-2">Duration</th>
                <th className="pb-2">Events</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_sessions.map((session) => {
                const start = parseISO(session.started_at);
                const end = parseISO(session.ended_at);
                const durationMins = Math.round(
                  (end.getTime() - start.getTime()) / 60000,
                );

                return (
                  <tr
                    key={session.event_session_id}
                    className="border-b border-gray-700/50"
                  >
                    <td className="py-3 font-mono text-sm">
                      <Link
                        to={`/messages?session_id=${encodeURIComponent(session.event_session_id)}`}
                        className="text-gray-300 hover:text-purple-400 transition-colors underline decoration-gray-600 hover:decoration-purple-400"
                      >
                        {session.event_session_id?.slice(0, 8) || "N/A"}
                      </Link>
                    </td>
                    <td className="py-3 text-sm">
                      {session.git_remote_url ? (
                        <Link
                          to={`/messages?git_remote=${encodeURIComponent(session.git_remote_url)}`}
                          className="text-gray-400 hover:text-purple-400 transition-colors underline decoration-gray-600 hover:decoration-purple-400"
                        >
                          {extractRepoName(session.git_remote_url)}
                        </Link>
                      ) : (
                        <span className="text-gray-500">local</span>
                      )}
                    </td>
                    <td className="py-3 text-gray-400">
                      {format(start, "MMM d, h:mm a")}
                    </td>
                    <td className="py-3 text-gray-400">
                      {durationMins < 60
                        ? `${durationMins}m`
                        : `${Math.floor(durationMins / 60)}h ${durationMins % 60}m`}
                    </td>
                    <td className="py-3 text-gray-300">
                      {session.event_count}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {stats.recent_sessions.length === 0 && (
            <p className="text-gray-500 text-center py-4">No sessions yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
